import { useState, useEffect, useRef, useMemo } from "react";
import { social, getPlatformInfo, type SocialAccount, type InboxItem } from "../lib/social";
import { PlatformIcon } from "../components/PlatformIcon";
import { useAccounts } from "../lib/app-data";
import { InboxListSkeleton } from "../components/Skeletons";

type FilterStatus = "all" | "unread" | "read" | "replied" | "archived";

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function StatusDot({ status }: { status: string }) {
  if (status === "unread") return <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />;
  if (status === "replied") return <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />;
  return null;
}

export function Inbox() {
  const [items, setItems] = useState<InboxItem[]>([]);
  const { accounts: allAccounts } = useAccounts();
  const accounts = useMemo<SocialAccount[]>(
    () => allAccounts.filter((a) => a.status === "active"),
    [allAccounts],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);

  // Filters
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [filterAccount, setFilterAccount] = useState<number | null>(null);
  const [filterPlatform, setFilterPlatform] = useState<string>("");

  // Reply state
  const [replyingTo, setReplyingTo] = useState<InboxItem | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [privateReply, setPrivateReply] = useState(false);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  // Selected item for detail
  const [selectedItem, setSelectedItem] = useState<InboxItem | null>(null);


  useEffect(() => {
    loadInbox();
  }, [filterStatus, filterAccount, filterPlatform]);

  useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);

  async function loadInbox() {
    setLoading(true);
    try {
      const params: Record<string, any> = { limit: 100, sort: "platform_created_at:desc" };
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterAccount) params.social_account_id = filterAccount;
      if (filterPlatform) params.platform = filterPlatform;
      // Don't show own comments by default
      params.is_own = false;

      const res = await social.listInbox(params);
      setItems(res.data || []);
      setTotal(res.total || 0);
      setUnreadCount(res.unread_count || 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkRead(item: InboxItem) {
    try {
      await social.updateInbox({ id: item.id, status: "read" });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "read" } : i)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  }

  async function handleMarkAllRead() {
    try {
      await social.updateInbox({ mark_all_read: true, social_account_id: filterAccount || undefined });
      setItems((prev) => prev.map((i) => (i.status === "unread" ? { ...i, status: "read" } : i)));
      setUnreadCount(0);
    } catch {}
  }

  async function handleArchive(item: InboxItem) {
    try {
      await social.updateInbox({ id: item.id, status: "archived" });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch {}
  }

  async function handleReply() {
    if (!replyingTo || !replyText.trim()) return;
    setReplying(true);
    try {
      await social.replyInbox({
        social_account_id: replyingTo.social_account_id,
        message: replyText.trim(),
        comment_id: replyingTo.comment_id,
        inbox_item_id: replyingTo.id,
        private_reply: privateReply || undefined,
      });
      // Update item status
      setItems((prev) =>
        prev.map((i) => (i.id === replyingTo.id ? { ...i, status: "replied" } : i))
      );
      setReplyingTo(null);
      setReplyText("");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setReplying(false);
    }
  }

  function openReply(item: InboxItem) {
    setReplyingTo(item);
    setReplyText("");
    setPrivateReply(false);
    // Mark as read when opening reply
    if (item.status === "unread") {
      handleMarkRead(item);
    }
  }

  function selectItem(item: InboxItem) {
    setSelectedItem(item);
    if (item.status === "unread") {
      handleMarkRead(item);
    }
  }

  // Unique platforms from accounts
  const platformOptions = [...new Set(accounts.map((a) => a.platform))];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-stone-900 dark:text-white tracking-tight">Inbox</h2>
          {unreadCount > 0 && (
            <span className="px-2.5 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-stone-500 dark:text-zinc-400 hover:text-orange-600 dark:hover:text-orange-400 font-medium transition-colors"
          >
            Mark all read
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 rounded-xl mb-4 text-sm border border-red-200/60 dark:border-red-500/20 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError("")} className="text-red-400 hover:text-red-600 text-xs font-medium ml-3">dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {/* Status filter */}
        {(["all", "unread", "read", "replied", "archived"] as FilterStatus[]).map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filterStatus === s
                ? "bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20"
                : "text-stone-500 dark:text-zinc-400 hover:bg-stone-100 dark:hover:bg-zinc-800 border border-transparent"
            }`}
          >
            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}

        <div className="w-px h-5 bg-stone-200 dark:bg-zinc-700 mx-1" />

        {/* Platform filter */}
        <select
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white dark:bg-zinc-800 border border-stone-200 dark:border-zinc-700 text-stone-600 dark:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
        >
          <option value="">All platforms</option>
          {platformOptions.map((p) => (
            <option key={p} value={p}>{getPlatformInfo(p).name}</option>
          ))}
        </select>

        {/* Account filter */}
        <select
          value={filterAccount || ""}
          onChange={(e) => setFilterAccount(e.target.value ? parseInt(e.target.value) : null)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white dark:bg-zinc-800 border border-stone-200 dark:border-zinc-700 text-stone-600 dark:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
        >
          <option value="">All accounts</option>
          {accounts.filter((a) => !filterPlatform || a.platform === filterPlatform).map((a) => (
            <option key={a.id} value={a.id}>{a.platform_display_name || a.platform_username}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <InboxListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState filterStatus={filterStatus} />
      ) : (
        <div className="flex gap-4">
          {/* Item list */}
          <div className="flex-1 space-y-1">
            {items.map((item) => (
              <InboxItemRow
                key={item.id}
                item={item}
                isSelected={selectedItem?.id === item.id}
                onSelect={() => selectItem(item)}
                onReply={() => openReply(item)}
                onArchive={() => handleArchive(item)}
              />
            ))}
          </div>

          {/* Detail panel */}
          {selectedItem && (
            <DetailPanel
              item={selectedItem}
              onReply={() => openReply(selectedItem)}
              onArchive={() => { handleArchive(selectedItem); setSelectedItem(null); }}
              onClose={() => setSelectedItem(null)}
            />
          )}
        </div>
      )}

      {/* Reply modal */}
      {replyingTo && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !replying && setReplyingTo(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-stone-200/60 dark:border-zinc-800/60" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-50 dark:bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-stone-900 dark:text-white">Reply to {replyingTo.from_name}</h3>
                <p className="text-xs text-stone-500 dark:text-zinc-500">
                  via {replyingTo.account?.platform_display_name || getPlatformInfo(replyingTo.platform).name}
                </p>
              </div>
            </div>

            {/* Original comment */}
            <div className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3 mb-4">
              <p className="text-xs font-medium text-stone-500 dark:text-zinc-500 mb-1">{replyingTo.from_name}</p>
              <p className="text-sm text-stone-700 dark:text-zinc-300">{replyingTo.message}</p>
            </div>

            {/* Reply type toggle */}
            {(replyingTo.platform === "facebook" || replyingTo.platform === "instagram") && (
              <div className="flex items-center gap-3 mb-3">
                <button
                  onClick={() => setPrivateReply(false)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    !privateReply
                      ? "bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20"
                      : "text-stone-500 dark:text-zinc-400 hover:bg-stone-100 dark:hover:bg-zinc-800 border border-transparent"
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Public reply
                </button>
                <button
                  onClick={() => setPrivateReply(true)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    privateReply
                      ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20"
                      : "text-stone-500 dark:text-zinc-400 hover:bg-stone-100 dark:hover:bg-zinc-800 border border-transparent"
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send as DM
                </button>
              </div>
            )}

            <textarea
              ref={replyInputRef}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={privateReply ? "Write a private message..." : "Write your reply..."}
              rows={3}
              disabled={replying}
              className="w-full px-4 py-3 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 resize-none text-sm disabled:opacity-50 transition-colors"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleReply();
              }}
            />

            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-stone-400 dark:text-zinc-600">
                Cmd+Enter to send
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setReplyingTo(null)}
                  disabled={replying}
                  className="px-4 py-2.5 text-sm font-medium text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800 rounded-xl transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReply}
                  disabled={replying || !replyText.trim()}
                  className="px-5 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition-all"
                >
                  {replying ? "Sending..." : "Reply"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InboxItemRow({
  item,
  isSelected,
  onSelect,
  onReply,
  onArchive,
}: {
  item: InboxItem;
  isSelected: boolean;
  onSelect: () => void;
  onReply: () => void;
  onArchive: () => void;
}) {
  const info = getPlatformInfo(item.platform);
  const isUnread = item.status === "unread";

  return (
    <div
      onClick={onSelect}
      className={`flex items-start gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all group ${
        isSelected
          ? "bg-orange-50 dark:bg-orange-500/5 border border-orange-200/60 dark:border-orange-500/20"
          : isUnread
          ? "bg-white dark:bg-zinc-900 border border-stone-200/60 dark:border-zinc-800/60 hover:border-orange-200 dark:hover:border-orange-500/20"
          : "bg-white dark:bg-zinc-900/50 border border-stone-100 dark:border-zinc-800/40 hover:border-stone-200 dark:hover:border-zinc-700"
      }`}
    >
      {/* Platform icon */}
      <div className="flex-shrink-0 mt-0.5">
        <span className={`w-8 h-8 ${info.color} text-white rounded-lg flex items-center justify-center`}>
          <PlatformIcon platform={item.platform} className="w-4 h-4" />
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <StatusDot status={item.status} />
          <span className={`text-sm font-medium truncate ${isUnread ? "text-stone-900 dark:text-white" : "text-stone-600 dark:text-zinc-300"}`}>
            {item.from_name || "Unknown"}
          </span>
          {item.type === "reply" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 dark:bg-zinc-800 text-stone-500 dark:text-zinc-500 font-medium">reply</span>
          )}
          {item.type === "story_reply" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-50 dark:bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 font-medium uppercase tracking-wide">
              Story
            </span>
          )}
          <span className="text-xs text-stone-400 dark:text-zinc-600 ml-auto flex-shrink-0">
            {timeAgo(item.platform_created_at || item.created_at)}
          </span>
        </div>

        <p className={`text-sm truncate ${isUnread ? "text-stone-700 dark:text-zinc-200" : "text-stone-500 dark:text-zinc-400"}`}>
          {item.message || "No message"}
        </p>

        <div className="flex items-center gap-2 mt-1">
          {item.account && (
            <span className="text-[10px] text-stone-400 dark:text-zinc-600 truncate">
              {item.account.platform_display_name}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onReply(); }}
          className="p-1.5 rounded-lg text-stone-400 dark:text-zinc-500 hover:text-orange-500 dark:hover:text-orange-400 hover:bg-stone-100 dark:hover:bg-zinc-800 transition-colors"
          title="Reply"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onArchive(); }}
          className="p-1.5 rounded-lg text-stone-400 dark:text-zinc-500 hover:text-stone-600 dark:hover:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800 transition-colors"
          title="Archive"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function DetailPanel({
  item,
  onReply,
  onArchive,
  onClose,
}: {
  item: InboxItem;
  onReply: () => void;
  onArchive: () => void;
  onClose: () => void;
}) {
  const info = getPlatformInfo(item.platform);

  return (
    <div className="w-80 flex-shrink-0 bg-white dark:bg-zinc-900 border border-stone-200/60 dark:border-zinc-800/60 rounded-2xl p-5 sticky top-8 self-start">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold text-stone-400 dark:text-zinc-600 uppercase tracking-widest">Detail</span>
        <button onClick={onClose} className="text-stone-400 dark:text-zinc-600 hover:text-stone-600 dark:hover:text-zinc-400 p-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Author */}
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-10 h-10 ${info.color} text-white rounded-xl flex items-center justify-center`}>
          <PlatformIcon platform={item.platform} className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-stone-900 dark:text-white truncate">{item.from_name || "Unknown"}</p>
          <p className="text-xs text-stone-400 dark:text-zinc-500">
            {info.name} &middot; {item.type}
          </p>
        </div>
      </div>

      {/* Message */}
      <div className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3 mb-4">
        <p className="text-sm text-stone-700 dark:text-zinc-300 whitespace-pre-wrap break-words">
          {item.message}
        </p>
      </div>

      {/* Meta */}
      <div className="space-y-2 mb-5 text-xs text-stone-500 dark:text-zinc-500">
        <div className="flex justify-between">
          <span>Time</span>
          <span className="text-stone-700 dark:text-zinc-300">
            {new Date(item.platform_created_at || item.created_at).toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Status</span>
          <span className={`font-medium ${
            item.status === "unread" ? "text-orange-600 dark:text-orange-400" :
            item.status === "replied" ? "text-emerald-600 dark:text-emerald-400" :
            "text-stone-600 dark:text-zinc-300"
          }`}>
            {item.status}
          </span>
        </div>
        {item.account && (
          <div className="flex justify-between">
            <span>Account</span>
            <span className="text-stone-700 dark:text-zinc-300 truncate ml-4">{item.account.platform_display_name}</span>
          </div>
        )}
        {item.post_id && (
          <div className="flex justify-between">
            <span>Post ID</span>
            <span className="text-stone-700 dark:text-zinc-300 font-mono text-[10px] truncate ml-4">{item.post_id}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onReply}
          className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition-all"
        >
          Reply
        </button>
        <button
          onClick={onArchive}
          className="px-4 py-2.5 text-stone-600 dark:text-zinc-400 bg-stone-100 dark:bg-zinc-800 rounded-xl text-sm font-medium hover:bg-stone-200 dark:hover:bg-zinc-700 transition-colors"
        >
          Archive
        </button>
      </div>
    </div>
  );
}

function EmptyState({ filterStatus }: { filterStatus: FilterStatus }) {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 bg-stone-100 dark:bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-stone-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      </div>
      <p className="text-stone-500 dark:text-zinc-500 mb-2">
        {filterStatus === "unread" ? "No unread messages" :
         filterStatus === "all" ? "No inbox items yet" :
         `No ${filterStatus} items`}
      </p>
      <p className="text-stone-400 dark:text-zinc-600 text-sm">
        Comments and replies from your social accounts will appear here
      </p>
    </div>
  );
}
